/**
 * Environment Variable Validation
 *
 * Validates required environment variables at application startup.
 * Provides type-safe access to environment variables throughout the app.
 */

interface EnvConfig {
  // Required
  SUPABASE_URL: string;
  SUPABASE_ANON_KEY: string;

  // Optional
  POSTHOG_KEY?: string;
  POSTHOG_HOST?: string;
  SENTRY_DSN?: string;
  SENTRY_DEV_ENABLED?: boolean;
  SENTRY_ENVIRONMENT?: string;
  SENTRY_RELEASE?: string;
}

interface EnvValidationResult {
  valid: boolean;
  errors: string[];
  config: EnvConfig | null;
}

/**
 * Validate environment variables and return typed config
 */
export function validateEnv(): EnvValidationResult {
  const errors: string[] = [];

  // Required variables
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl) {
    errors.push("VITE_SUPABASE_URL is required");
  } else if (!supabaseUrl.startsWith("https://")) {
    errors.push("VITE_SUPABASE_URL must start with https://");
  }

  if (!supabaseAnonKey) {
    errors.push("VITE_SUPABASE_ANON_KEY is required");
  } else if (supabaseAnonKey.length < 100) {
    errors.push("VITE_SUPABASE_ANON_KEY appears to be invalid (too short)");
  }

  // Optional variables
  const posthogKey =
    import.meta.env.VITE_PUBLIC_POSTHOG_KEY?.trim() ||
    import.meta.env.VITE_POSTHOG_KEY?.trim();
  const posthogHost =
    import.meta.env.VITE_PUBLIC_POSTHOG_HOST?.trim() ||
    import.meta.env.VITE_POSTHOG_HOST?.trim();
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  const sentryDevEnabled = import.meta.env.VITE_SENTRY_DEV_ENABLED === "true";
  const sentryEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT?.trim();
  const sentryRelease = import.meta.env.VITE_SENTRY_RELEASE?.trim();

  if (posthogHost && !posthogHost.startsWith("https://")) {
    errors.push(
      "VITE_PUBLIC_POSTHOG_HOST (or VITE_POSTHOG_HOST) must start with https://",
    );
  }

  if (sentryDsn && !sentryDsn.startsWith("https://")) {
    errors.push("VITE_SENTRY_DSN must be a valid Sentry DSN URL");
  }

  if (sentryEnvironment && !/^[A-Za-z0-9._-]+$/.test(sentryEnvironment)) {
    errors.push(
      "VITE_SENTRY_ENVIRONMENT may only contain letters, numbers, '.', '_' and '-'",
    );
  }

  if (sentryRelease && sentryRelease.length > 200) {
    errors.push("VITE_SENTRY_RELEASE must be 200 characters or fewer");
  }

  if (errors.length > 0) {
    return {
      valid: false,
      errors,
      config: null,
    };
  }

  return {
    valid: true,
    errors: [],
    config: {
      SUPABASE_URL: supabaseUrl,
      SUPABASE_ANON_KEY: supabaseAnonKey,
      POSTHOG_KEY: posthogKey || undefined,
      POSTHOG_HOST: posthogHost || undefined,
      SENTRY_DSN: sentryDsn || undefined,
      SENTRY_DEV_ENABLED: sentryDevEnabled,
      SENTRY_ENVIRONMENT: sentryEnvironment || undefined,
      SENTRY_RELEASE: sentryRelease || undefined,
    },
  };
}

/**
 * Get validated environment config
 * Throws if environment is invalid
 */
export function getEnvConfig(): EnvConfig {
  const result = validateEnv();

  if (!result.valid) {
    const errorMessage = `Environment validation failed:\n${result.errors.map((e) => `  - ${e}`).join("\n")}`;

    // In development, show error in console
    if (import.meta.env.DEV) {
      console.error(errorMessage);
    }

    throw new Error(errorMessage);
  }

  return result.config!;
}

/**
 * Check if we're in production mode
 */
export function isProduction(): boolean {
  return import.meta.env.PROD;
}

/**
 * Check if we're in development mode
 */
export function isDevelopment(): boolean {
  return import.meta.env.DEV;
}

/**
 * Get the current environment mode
 */
export function getMode(): string {
  return import.meta.env.MODE;
}
